<template>
	<div class="price-details">
		<div class="price-details__header">
			<p
				v-if="priceDifference != 'equal' && offerStatus === 'Available'"
				class="price-details__price-change"
				:class="priceDifference === 'lower' ? 'price-details__price-change--lower' : 'price-details__price-change--higher'"
			>
				{{ priceChangeText }}
			</p>
		</div>
		<ul class="price-details__list">
			<template v-if="travellerPrices && travellerPrices.length">
				<li
					v-for="(traveller, index) in travellerPrices"
					:key="index"
					class="price-details__list-item"
				>
					<span class="price-details__item-type">{{ traveller.Label }}</span>
					<span class="price-details__item-price">{{ traveller.Price }}</span>
				</li>
			</template>
		</ul>

		<div class="price-details__footer">
			<div class="price-details__price-label-container">
				<div class="price-details__summary">
					<span class="price-details__price-label"> Gesamtpreis </span>
					<p class="price-details__price">
						{{ finalPrice }}
					</p>
				</div>
				<div class="price-details__payments">
					<img
						v-for="(item, index) in paymentTypes"
						:key="index"
						class="price-details__payment-img"
						:src="item.img"
						:title="item.name"
						:alt="item.name"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
import type { MergedOfferItemData, TravellerPrice } from '@interfaces/offer';
import { getPaymentUrl } from '@utils/environmentUtils';
import { formatPrice, getFinalPriceWithCurrency } from '@/js/utils/priceUtils';
import { PAYMENT_TYPES } from '@global-js/constants';
import { computed } from 'vue';

interface Props {
	offerData: MergedOfferItemData;
}

const props = defineProps<Props>();

const travellerAmount = computed((): number => props.offerData.TravellerAmount);

const offerStatus = computed((): string | undefined => props.offerData.Status);

const offerCurrency = computed((): string => props.offerData.CurrencyCode);

const offerPrice = computed((): number | null => props.offerData.Price);

const finalPrice = computed((): string => {
	const verified = {
		price: offerPrice.value,
		currency: offerCurrency.value,
	};

	const unverified = {
		price: props.offerData.unverifiedPrice,
		currency: props.offerData.unverifiedCurrency,
		travellerAmount: travellerAmount.value,
	};

	return getFinalPriceWithCurrency(offerStatus.value as string, verified, unverified);
});

const priceDifference = computed((): string => {
	if (!offerPrice.value) return '';
	const assumedPrice = props.offerData.unverifiedPrice * travellerAmount.value;
	const strippedFinalPrice = parseFloat(offerPrice.value.toString());

	if (assumedPrice > strippedFinalPrice) {
		return assumedPrice - strippedFinalPrice > 10 ? 'lower' : 'equal';
	}
	if (assumedPrice < strippedFinalPrice) {
		return strippedFinalPrice - assumedPrice > 10 ? 'higher' : 'equal';
	}
	return 'equal';
});

const priceChangeText = computed((): string => {
	if (priceDifference.value === 'lower') {
		return 'Angebot wurde günstiger';
	}
	if (priceDifference.value === 'higher') {
		return 'Preisänderung des Veranstalters';
	}
	return '';
});

const travellerPrices = computed((): TravellerPrice[] | undefined => {
	if (!props.offerData.TravellerPrice) {
		return undefined;
	}

	if (offerStatus.value === 'CheckUnable' || offerStatus.value === 'Undefined') {
		return [
			{
				Label: 'Alle Personen',
				Price: finalPrice.value,
			},
		];
	}

	return props.offerData.TravellerPrice.map((traveller: TravellerPrice) => ({
		Label: traveller.Label,
		Price: formatPrice(traveller.Price as number, props.offerData.CurrencyCode, true),
	}));
});

const paymentTypes = computed((): Record<string, string>[] => {
	const url = getPaymentUrl();
	const types = PAYMENT_TYPES;

	return props.offerData.AvailablePaymentTypes.map((payment: string) => {
		const type = types[payment];

		return {
			img: `${url}/${type.img}.png`,
			name: type.label,
		};
	});
});
</script>

<style lang="scss" scoped>
.price-details {
	color: $color-text;
	font-size: $font-small-2;

	&__footer {
		margin-top: 0.8rem;
		display: flex;
		justify-content: flex-end;
	}

	&__header {
		margin-bottom: 0.4rem;
		width: auto;
		display: flex;
		justify-content: flex-end;
	}

	&__price-label-container {
		width: 100%;
	}

	&__price-change {
		min-width: fit-content;
		font-size: $font-small-4;
		font-weight: $font-weight-semibold;
		padding: 0.3rem 0.7rem;
		letter-spacing: 0.03rem;
		border-radius: $border-radius-small;
		max-width: fit-content;

		&--higher {
			background-color: $color-warning;
			color: $color-white;
		}

		&--lower {
			background-color: $color-lime-light;
			color: $color-text;
		}
	}

	&__price-label {
		font-size: $font-small-2;
		font-weight: $font-weight-semibold;
	}

	&__list {
		margin: 0;
		padding: 0 0 0.8rem;
		border-bottom: 0.1rem solid $color-primary-l4;
		list-style: none;
	}

	&__list-item {
		display: flex;
		margin-bottom: 0.5rem;
	}

	&__item-type,
	&__item-price {
		width: 50%;
		font-size: $font-small-1;
		font-weight: $font-weight-regular;

		@include media-query-up($breakpoint-small) {
			font-size: $font-small-2;
		}
	}

	&__item-price {
		text-align: right;

		@include media-query-up($breakpoint-medium) {
			text-align: right;
		}
	}

	&__summary {
		display: flex;
		gap: 1rem;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 0.4rem;
	}

	&__price {
		margin: 0;
		color: $color-black;
		font-size: $font-medium-3;
		font-weight: $font-weight-semibold;

		@include media-query-up($breakpoint-extralarge) {
			margin: 0 0 0.6rem;
			font-size: $font-medium-1;
		}
	}

	&__price-hint {
		margin: 0 0 0.5rem;
		font-size: $font-small-4;
		font-weight: $font-weight-regular;
		text-align: right;

		@include media-query-up($breakpoint-small) {
			font-size: $font-small-3;
		}
	}

	&__payments {
		text-align: end;
		padding: 0.4rem 0;
	}

	&__payment-img {
		height: 2.2rem;
		margin-right: 0.5rem;

		&:last-of-type {
			margin-right: 0;
		}
	}

	@media (min-width: $breakpoint-verylarge) {
		&__header {
			display: flex;
			justify-content: flex-end;
			margin-bottom: 1rem;
		}

		&__price-change {
			margin-left: 0.4rem;
		}
	}
}
</style>
